<!--
  RailButton — single icon button inside the CauldronRail.

  Renders a `<router-link>` wrapped icon. Active state is computed by the
  rail (which already has the `useActiveCauldron` resolver) and passed in
  via the `active` prop, so this component stays pure presentational and
  doesn't repeat the cauldron-detection logic.
-->
<template>
  <component
    :is="disabled ? 'span' : 'router-link'"
    :to="disabled ? undefined : to"
    :role="disabled ? 'link' : undefined"
    :aria-disabled="disabled ? 'true' : undefined"
    :tabindex="disabled ? -1 : undefined"
    class="rail-button group relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors focus:outline-none focus-visible:shadow-theme-focus"
    :class="[
      disabled ? 'opacity-50 cursor-not-allowed' : '',
      active
        ? 'bg-theme-palette-emerald/10 text-theme-palette-emerald dark:bg-theme-palette-emerald/30 dark:text-theme-palette-emerald'
        : 'text-theme-text-secondary hover:text-theme-text hover:bg-theme-bg-subtle dark:hover:text-theme-text-muted dark:hover:bg-theme-bg/60',
    ]"
    :aria-label="label"
    :title="tooltip || label"
    @mouseenter="!disabled && $emit('hover')"
    @mouseleave="$emit('unhover')"
    @focus="!disabled && $emit('hover')"
    @blur="$emit('unhover')"
  >
    <icon :path="icon" size="20" />
    <!-- Active-indicator pip on the left edge so the cauldron is still
         legible even when the icon's emerald tint blends with the page. -->
    <span
      v-if="active"
      class="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-theme-palette-emerald"
      aria-hidden="true"
    />
  </component>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';

  defineProps({
    to: { type: [String, Object], required: true },
    icon: { type: String, required: true },
    label: { type: String, required: true },
    tooltip: { type: String, default: '' },
    active: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  });
  defineEmits(['hover', 'unhover']);
</script>
